import { InfoBox } from "@tfg/ui"
import { createContext, useState, useCallback } from "react"

import {
  ProductSheetLanguage,
  ProductSheetLanguages,
} from "@tfg/types/constants/language.constants"
import { ComponentContent } from "@tfg/types/schemas/communication-studio/components"
import { ComponentTypeKey } from "@tfg/types/types/communication-studio/components.types"
import { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import { useGetProductPageComponents } from "~/gen/api/hooks"
import { GetProductPageComponentsResponseDto } from "~/gen/api/types"
import { useProductSheetGroup } from "~/pages/Brand/CommunicationStudio/contexts/productSheetGroup/useProductSheetGroup"

export type ComponentModalMode = "create" | "edit"

export type ComponentModalState = {
  isOpen: boolean
  mode: ComponentModalMode
  componentId?: string
  componentType: ComponentTypeKey | null
  textContent: ComponentContent | null
  configurations: Partial<ContentFilteringProps> | null
}

export type ComponentCreationContextType = {
  isComponentCreationModalOpen: boolean
  setIsComponentCreationModalOpen: (isOpen: boolean) => void
  componentType: ComponentTypeKey | null
  setComponentType: (componentType: ComponentTypeKey | null) => void
  textContent: ComponentContent | null
  setTextContent: (textContent: ComponentContent | null) => void

  modalState: ComponentModalState
  openModal: (
    component: GetProductPageComponentsResponseDto | null,
    mode: ComponentModalMode
  ) => void
  closeModal: () => void
  updateModalContent: (content: ComponentContent) => void
  updateModalConfigurations: (
    configurations: Partial<ContentFilteringProps> | null
  ) => void
  updateModalComponentType: (componentType: ComponentTypeKey | null) => void
  language: ProductSheetLanguage
  setLanguage: (language: ProductSheetLanguage) => void
  productPageComponents: GetProductPageComponentsResponseDto[]
  error: Error | null
  isLoadingProductPageComponents: boolean
  isRefetchingProductPageComponents: boolean
  productSheetGroupId: string
}

export const ComponentCreationProvider = ({
  productSheetGroupId,
  children,
}: {
  productSheetGroupId: string
  children: React.ReactNode
}) => {
  const [isComponentCreationModalOpen, setIsComponentCreationModalOpen] =
    useState(false)
  const [componentType, setComponentType] = useState<ComponentTypeKey | null>(
    null
  )
  const [textContent, setTextContent] = useState<ComponentContent | null>(null)

  const [modalState, setModalState] = useState<ComponentModalState>({
    isOpen: false,
    mode: "create",
    componentType: null,
    textContent: null,
    configurations: null,
  })

  const [language, setLanguage] = useState<ProductSheetLanguage>(
    ProductSheetLanguages.EN
  )
  const { selectedProductSheetGroup } = useProductSheetGroup()

  const {
    data: productPageComponents,
    isPending: isLoadingProductPageComponents,
    isRefetching: isRefetchingProductPageComponents,
    error,
  } = useGetProductPageComponents(selectedProductSheetGroup?.id ?? "", {
    query: {
      enabled: !!selectedProductSheetGroup?.id,
    },
  })

  const openModal = useCallback(
    (
      component: GetProductPageComponentsResponseDto | null,
      mode: ComponentModalMode
    ) => {
      if (mode === "edit") {
        if (!component) return
        setModalState({
          isOpen: true,
          mode,
          componentId: component.id,
          componentType: component.componentType,
          textContent: component.content ?? null,
          configurations: component.configurations,
        })
      } else {
        setModalState((prev) => ({
          ...prev,
          isOpen: true,
          mode,
          componentType: null,
          textContent: null,
          configurations: null,
        }))
      }
    },
    []
  )

  const closeModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      isOpen: false,
    }))
    setTimeout(() => {
      setModalState({
        isOpen: false,
        mode: "create",
        componentType: null,
        textContent: null,
        configurations: null,
      })
    }, 300)
  }, [])

  const updateModalContent = useCallback((content: ComponentContent) => {
    setModalState((prev) => ({
      ...prev,
      textContent: content,
    }))
  }, [])

  const updateModalConfigurations = useCallback(
    (configurations: Partial<ContentFilteringProps> | null) => {
      setModalState((prev) => ({
        ...prev,
        configurations,
      }))
    },
    []
  )

  const updateModalComponentType = useCallback(
    (componentType: ComponentTypeKey | null) => {
      setModalState((prev) => ({
        ...prev,
        componentType,
      }))
    },
    []
  )

  return (
    <ComponentCreationContext.Provider
      value={{
        isComponentCreationModalOpen,
        setIsComponentCreationModalOpen,
        componentType,
        setComponentType,
        textContent,
        setTextContent,

        // New modal management
        modalState,
        openModal,
        closeModal,
        updateModalContent,
        updateModalConfigurations,
        updateModalComponentType,
        // Common state
        language,
        setLanguage,
        productPageComponents: productPageComponents?.data ?? [],
        isLoadingProductPageComponents,
        isRefetchingProductPageComponents,
        productSheetGroupId,
        error,
      }}
    >
      {selectedProductSheetGroup ? (
        children
      ) : (
        <InfoBox
          title="No product sheet group selected"
          message="Please contact support"
          variant="warning"
          width="fit-content"
        />
      )}
    </ComponentCreationContext.Provider>
  )
}

export const ComponentCreationContext = createContext<
  ComponentCreationContextType | undefined
>(undefined)
